import React, { useEffect, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';

import { warehouseApi as api } from 'api/warehouse';
import { Alert, AlertDescription } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'shadcn/components/ui/Dialog';

/**
 * Confirm-and-clear dialog for all OUT_OF_STOCK slots on a pack line.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {number|string} props.packLineId
 * @param {(result: { successCount: number }) => void} [props.onSuccess]
 */
function ClearPackLineOutOfStock({ onOpenChange, onSuccess, open, packLineId }) {
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!open) {
            return;
        }
        setError('');
        setIsSubmitting(false);
    }, [open, packLineId]);

    const handleOpenChange = (nextOpen) => {
        if (isSubmitting && !nextOpen) {
            return;
        }
        onOpenChange(nextOpen);
    };

    const handleContinue = async () => {
        if (isSubmitting || packLineId == null) {
            return;
        }

        setError('');
        setIsSubmitting(true);
        try {
            const res = await api.clearPackLineOutOfStock({ packLineId });

            if (!res.status) {
                setError(res.msg || 'Failed to clear out of stock items');
                return;
            }

            onSuccess?.({ successCount: res.data?.successCount ?? 0 });
            onOpenChange(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                className="sm:max-w-md"
                onEscapeKeyDown={(e) => {
                    if (isSubmitting) {
                        e.preventDefault();
                    }
                }}
                onPointerDownOutside={(e) => {
                    if (isSubmitting) {
                        e.preventDefault();
                    }
                }}
            >
                <DialogHeader>
                    <DialogTitle>Clear all out of stock?</DialogTitle>
                    <DialogDescription>
                        This will clear all out of stock flags from the current Pack Line. Continue?
                    </DialogDescription>
                </DialogHeader>

                {error ? (
                    <Alert variant="destructive">
                        <AlertCircle />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                ) : null}

                <DialogFooter className="gap-2 sm:gap-0">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleOpenChange(false)}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button type="button" onClick={handleContinue} disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Clearing…
                            </>
                        ) : (
                            'Continue'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export { ClearPackLineOutOfStock };
